import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { AuthStore } from 'src/core/stores';
import {
  CoreUsersService,
  CreateUserDto,
  UpdateUserDto,
  UserDto,
} from 'src/generated/api/nestjs-backend';
import { DialogComponent } from '../../shared/dialog/dialog.component';

@Component({
  selector: 'core-edit-user-dialog',
  templateUrl: './edit-user-dialog.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    DialogComponent,
    NgClass,
    NgTemplateOutlet,
  ],
})
export class EditUserDialogComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private elementRef = inject(ElementRef);
  private authStore = inject(AuthStore);
  private coreUsersService = inject(CoreUsersService);

  @ViewChild(DialogComponent) dialogComponent!: DialogComponent;
  @Output() save: EventEmitter<unknown> = new EventEmitter<unknown>();
  @Input() user!: UserDto;
  @Input() createOrEditDialog!: boolean;
  private onClose = new EventEmitter<boolean>();
  public userDialogTitle = 'edit-user-dialog-title';
  public userDialogSubtitle = 'edit-user-dialog-subtitle';
  public userDialogConfirmButtonText = 'core.buttons.save';
  public userDialogCancelButtonText = 'core.buttons.cancel';
  public showPassword = false;
  public userForm!: FormGroup;

  ngOnInit() {
    if (!this.createOrEditDialog) {
      this.userDialogTitle = 'delete-user-dialog-title';
      this.userDialogSubtitle = 'delete-user-dialog-subtitle';
      this.userDialogConfirmButtonText = 'core.buttons.delete';
      this.userDialogCancelButtonText = 'core.buttons.cancel';
    } else {
      this.initFormValidation();
    }
  }

  ngOnDestroy() {
    this.elementRef.nativeElement.remove();
  }

  ngAfterViewInit() {
    this.dialogComponent.onOpen();
  }

  onSave() {
    if (this.createOrEditDialog) {
      if (this.user.uuid) {
        this.updateUser();
      } else {
        this.createUser();
      }
    } else {
      this.deleteUser();
    }
  }

  closeModal(success: boolean) {
    this.onClose.emit(success);
  }

  getInputType(): string {
    return this.showPassword ? 'text' : 'password';
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  generatePassword(): void {
    const randomPassword = crypto.randomUUID();
    this.userFormPassword!.setValue(randomPassword);
  }

  get userFormFirstName() {
    return this.userForm.get('userFormFirstName');
  }

  get userFormLastName() {
    return this.userForm.get('userFormLastName');
  }

  get userFormEmail() {
    return this.userForm.get('userFormEmail');
  }

  get userFormPassword() {
    return this.userForm.get('userFormPassword');
  }

  get userFormCompanyName() {
    return this.userForm.get('userFormCompanyName');
  }

  get userFormBusinessUnit() {
    return this.userForm.get('userFormBusinessUnit');
  }

  private initFormValidation() {
    if (this.user) {
      if (!this.user.email) {
        this.userDialogTitle = 'create-user-dialog-title';
        this.userDialogSubtitle = 'create-user-dialog-subtitle';
        this.userDialogConfirmButtonText = 'core.buttons.add';
      }
      this.userForm = new FormGroup({
        userFormFirstName: new FormControl(this.user.firstName, {
          validators: [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(64),
          ],
        }),
        userFormLastName: new FormControl(this.user.lastName),
        userFormEmail: new FormControl(this.user.email, {
          validators: [Validators.required, Validators.email],
        }),
        userFormPassword: new FormControl(),
        userFormCompanyName: new FormControl(
          this.user?.companyName ? this.user.companyName : '',
          {
            validators: [Validators.required],
          },
        ),
        userFormBusinessUnit: new FormControl(
          this.user?.businessUnit ? this.user.businessUnit : '',
          {
            updateOn: 'blur',
          },
        ),
      });
    }
  }

  private createUser() {
    //TODO Move the randomUUID password generation to the backend
    const createdUser: CreateUserDto = {
      firstName:
        this.userFormFirstName?.value === '' ||
        this.userFormFirstName?.value === null
          ? undefined
          : this.userFormFirstName?.value,
      lastName:
        this.userFormLastName?.value === '' ||
        this.userFormLastName?.value === null
          ? undefined
          : this.userFormLastName?.value,
      email:
        this.userFormEmail?.value === '' || this.userFormEmail?.value === null
          ? undefined
          : this.userFormEmail?.value,
      password: crypto.randomUUID(),
      companyName:
        this.userFormCompanyName?.value === '' ||
        this.userFormCompanyName?.value === null
          ? undefined
          : this.userFormCompanyName?.value,
      businessUnit:
        this.userFormBusinessUnit?.value === '' ||
        this.userFormBusinessUnit?.value === null
          ? undefined
          : this.userFormBusinessUnit?.value,
    };

    this.coreUsersService
      .createUser({
        xTenantSlug: this.authStore.tenantSlug(),
        createUserDto: createdUser,
      })
      .subscribe({
        next: () => {
          this.closeModal(true);
        },
        error: (error) => {
          console.error(
            'Error while closing the dialog window: ',
            error.error?.message,
          );
        },
      });
  }

  private updateUser() {
    const updatedUser: UpdateUserDto = {
      firstName:
        this.userFormFirstName?.value === '' ||
        this.userFormFirstName?.value === null
          ? undefined
          : this.userFormFirstName?.value,
      lastName:
        this.userFormLastName?.value === '' ||
        this.userFormLastName?.value === null
          ? undefined
          : this.userFormLastName?.value,
      email:
        this.userFormEmail?.value === '' || this.userFormEmail?.value === null
          ? undefined
          : this.userFormEmail?.value,
      password:
        this.userFormPassword?.value === '' ||
        this.userFormPassword?.value === null
          ? undefined
          : this.userFormPassword?.value,
      companyName:
        this.userFormCompanyName?.value === '' ||
        this.userFormCompanyName?.value === null
          ? undefined
          : this.userFormCompanyName?.value,
      businessUnit:
        this.userFormBusinessUnit?.value === '' ||
        this.userFormBusinessUnit?.value === null
          ? undefined
          : this.userFormBusinessUnit?.value,
    };

    this.coreUsersService
      .updateUser({
        xTenantSlug: this.authStore.tenantSlug(),
        userUuid: this.user?.uuid,
        updateUserDto: updatedUser,
      })
      .subscribe({
        next: () => {
          this.closeModal(true);
        },
        error: (error) => {
          console.error(
            'Error while closing the dialog window: ',
            error.error?.message,
          );
        },
      });
  }

  private deleteUser() {
    this.coreUsersService
      .deleteUser({
        xTenantSlug: this.authStore.tenantSlug(),
        userUuid: this.user?.uuid,
      })
      .subscribe({
        next: () => {
          this.closeModal(true);
        },
        error: (error) => {
          console.error(
            'Error while closing the dialog window: ',
            error.error?.message,
          );
        },
      });
  }
}
